import { useEffect, useMemo, useState } from 'react';

import { GET_USER } from '@/services/graphql/queries';

import { AccessScope, CompanyStatus, UserAccountStatus, UserRole } from '../../types';
import { GetUserQuery } from '../../types/gql/graphql';
import { useFirebase } from '../context/firebase';
import { UserContext } from '../context/user';
import useEnhancedQuery from '../use-enhanced-query';
import useNumberFormat from '../use-number-format';

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const { authenticated } = useFirebase();
  const [isRegistering, setIsRegistering] = useState(false);
  const {
    data,
    loading: loadingUserData,
    refetch,
  } = useEnhancedQuery<GetUserQuery>(GET_USER, {
    fetchPolicy: 'cache-and-network',
    skip: !authenticated || isRegistering,
  });

  useEffect(() => {
    if (authenticated && !isRegistering) {
      refetch();
    }
  }, [authenticated, isRegistering, refetch]);

  const { setNumberFormat } = useNumberFormat();

  if (data && data.user) setNumberFormat(data.user.numberFormat);

  const hasCompleteProfile = data?.user?.status === UserAccountStatus.USER_INFO_COMPLETED;

  const hasCompanyOnboarded =
    data?.user?.role !== UserRole.ADMIN ||
    (data?.user?.role === UserRole.ADMIN && data?.company?.status === CompanyStatus.COMPANY_ACTIVE);

  const isAdmin = data?.user?.role === UserRole.ADMIN;
  const isViewer = data?.user?.role === UserRole.VIEWER;
  const isDeactivated =
    !data?.user?.activated || data?.company?.status === CompanyStatus.COMPANY_DEACTIVATED;

  const isMaterialityScope = data?.company?.accessScope === AccessScope.MATERIALITY;
  const contextValue = useMemo(
    () => ({
      hasCompleteProfile,
      user: data?.user,
      company: data?.company,
      loadingUserData,
      refetch,
      hasCompanyOnboarded,
      isAdmin,
      isViewer,
      isDeactivated,
      isMaterialityScope,
      setIsRegistering,
      isRegistering,
    }),
    [
      data?.company,
      data?.user,
      hasCompanyOnboarded,
      hasCompleteProfile,
      isAdmin,
      isDeactivated,
      isMaterialityScope,
      isRegistering,
      isViewer,
      loadingUserData,
      refetch,
      setIsRegistering,
    ]
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};
